import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { useMeasure } from 'react-use'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'relative inline-flex items-center py-spacing-2xs px-spacing-xs min-h-[4.3rem] lg:px-spacing-sm [&>*]:text-current [&>*:not([data-background])]:relative [&>*:not([data-background])]:z-20 select-none'
  ],
  {
    variants: {
      variant: {
        accent:
          'text-text-button-primary-light [&_svg[data-background]]:fill-surface-accent-light [&_span[data-background]]:bg-surface-accent-light',
        primary:
          'text-text-button-primary-light [&_svg[data-background]]:fill-surface-primary-light [&_span[data-background]]:bg-surface-primary-light',
        secondary:
          '[&_span[data-background]]:border [&_span[data-background]]:border-border-primary-light [&_svg[data-background]]:stroke-border-primary-light [&_svg[data-background]]:fill-transparent text-text-primary-light',
        switch:
          'text-text-button-primary-light [&_svg[data-background]]:fill-surface-switch-website-button-light [&_span[data-background]]:bg-surface-switch-website-button-light'
      }
    },
    defaultVariants: {
      variant: 'accent'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    endIcon?: ReactElement
    isLiquid?: boolean
    isActive?: boolean
    full?: boolean
    isInverted?: boolean
    strokeWidth?: number
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant,
      endIcon,
      isLiquid = false,
      isActive = false,
      full = false,
      isInverted = false,
      className,
      asChild = false,
      strokeWidth = 1,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'button'
    const [buttonRef, { width, height }] = useMeasure<HTMLButtonElement>()

    const getPath = (padding: number) => {
      const format = (num: number) => Number(num.toFixed(2))
      const radius = format(Math.min(width, height) * 0.2)
      const curveHeightUp = format(height * 0.05)
      const curveHeightDown = format(height * 0.09)

      return `
        M ${radius + padding} ${padding} 
        Q ${format(width) / 2 + padding} ${curveHeightUp + padding} ${format(width) - radius + padding} ${padding}
        H ${format(width) - radius + padding} 
        A ${radius} ${radius} 0 0 1 ${format(width) + padding} ${radius + padding}
        V ${format(height) - radius + padding}
        A ${radius} ${radius} 0 0 1 ${format(width) - radius + padding} ${format(height) + padding}
        H ${width - radius + padding} 
        Q ${format(width) / 2 + padding} ${format(height) - curveHeightDown + padding} ${radius + padding} ${format(height) + padding}
        H ${radius + padding}
        A ${radius} ${radius} 0 0 1 ${padding} ${format(height) - radius + padding}
        V ${radius + padding}
        A ${radius} ${radius} 0 0 1 ${radius + padding} ${padding}
        Z
      `
    }

    const bgClasses = cn(
      'absolute inset-0 z-10',
      'transition-transform duration-700 ease-cubic-default origin-center transform-cpu',
      'group-hover:scale-95'
    )

    return (
      <span
        className={cn([
          'group relative inline-block',
          { block: full, 'pointer-events-none': isActive }
        ])}
        ref={buttonRef}
      >
        <Component
          ref={ref}
          className={cn(buttonVariants({ variant, className }), {
            '[&_span[data-background]]:bg-surface-primary-dark [&_span]:text-text-button-primary-dark [&_svg[data-background]]:fill-surface-primary-dark':
              isInverted && variant === 'primary',
            'text-text-primary-invert-light [&_span[data-background]]:border-border-primary-invert-light [&_svg[data-background]]:stroke-border-primary-invert-light':
              isInverted && variant === 'secondary',
            'flex whitespace-normal': full,
            'items-center gap-[1.6rem]': endIcon
          })}
          {...props}
        >
          {isLiquid && isCorporate ? (
            <svg
              width={width + strokeWidth}
              height={height + strokeWidth}
              viewBox={`0 0 ${width + strokeWidth} ${height + strokeWidth}`}
              className={bgClasses}
              preserveAspectRatio="none"
              data-background
            >
              <path d={getPath(strokeWidth / 2)} />
            </svg>
          ) : (
            <span
              data-background
              className={cn('h-full w-full rounded-radius-rounded', bgClasses)}
            ></span>
          )}
          <Slottable>{children}</Slottable>
          {endIcon}
        </Component>
      </span>
    )
  }
)

export default Button
